import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// UI Components
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

// Data
import { industrySolutions } from '../../data/mockData';

/**
 * Industries listing page component
 */
const IndustriesPage: React.FC = () => {
  // Function to get industry-specific icons
  const getIndustryIcon = (industryId: string) => {
    switch (industryId) {
      case 'healthcare':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      case 'finance':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'retail':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5.5M7 13v8a2 2 0 002 2h6a2 2 0 002-2v-8m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v4.1" />
          </svg>
        );
      case 'manufacturing':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'education':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18.1 7.5 18.1s3.332.477 4.5 1.1m0-13C13.168 5.477 14.754 5 16.5 5s3.332.477 4.5 1.253v13C19.832 18.477 18.246 18.1 16.5 18.1s-3.332.477-4.5 1.1" />
          </svg>
        );
      case 'legal':
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        );
      default:
        return (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
    }
  };

  return (
    <Layout>
      {/* Hero Section with Modern Gradient */}
      <Section className="relative bg-gradient-to-br from-primary via-secondary to-accent overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-32 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 border border-white/10 rounded-full"></div>
        </div>
        
        <Container className="relative z-10 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Industry-Specific 
              <span className="block bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">
                AI Solutions
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl mb-12 text-white/90 font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Tailored AI implementations that address the unique challenges 
              <br className="hidden md:block" />
              and opportunities in your industry
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Button 
                variant="primary" 
                size="lg"
                className="bg-white text-primary hover:bg-white/90 border-0 shadow-xl hover:shadow-2xl transition-all duration-300"
                onClick={() => document.getElementById('solutions-grid')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Solutions
                <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </Button>
            </motion.div>
          </div>
        </Container>
      </Section>
      
      {/* Main Content - Industry Solutions Grid */}
      <Section id="solutions-grid" className="py-20 bg-gradient-to-b from-neutral-50 to-white">
        <Container>
          {/* Section Header */}
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
              Choose Your Industry
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Select your industry to discover AI solutions tailored to your specific challenges, 
              regulations, and opportunities.
            </p>
          </motion.div>

          {/* Industry Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industrySolutions.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group h-full"
              >
                <Link to={industry.ctaLink} className="block h-full">
                  <Card className="h-full bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:border-primary/20 overflow-hidden">
                    {/* Header with Icon and Title */}
                    <div className="relative p-8 bg-gradient-to-br from-primary/5 to-secondary/5 group-hover:from-primary/10 group-hover:to-secondary/10 transition-all duration-300">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 bg-white shadow-md rounded-xl group-hover:shadow-lg transition-all duration-300 text-primary">
                          {getIndustryIcon(industry.id)}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </div>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-neutral-900 mb-3 group-hover:text-primary transition-colors duration-300">
                        {industry.title}
                      </h3>
                    </div>
                    
                    {/* Content */}
                    <div className="p-8 -mt-2">
                      <p className="text-neutral-600 mb-6 text-lg leading-relaxed">
                        {industry.description}
                      </p>
                      
                      {/* Use Cases */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-neutral-800 mb-4 text-lg">Key Use Cases:</h4>
                        <ul className="space-y-3">
                          {industry.useCases.slice(0, 4).map((useCase, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="flex-shrink-0 w-5 h-5 mr-3 mt-0.5">
                                <svg className="w-full h-full text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <span className="text-neutral-600">{useCase}</span>
                            </li>
                          ))}
                          {industry.useCases.length > 4 && (
                            <li className="flex items-start text-primary font-medium">
                              <div className="flex-shrink-0 w-5 h-5 mr-3 mt-0.5">
                                <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </div>
                              <span>+{industry.useCases.length - 4} more solutions</span>
                            </li>
                          )}
                        </ul>
                      </div>
                      
                      {/* CTA */}
                      <div className="mt-auto pt-4 border-t border-neutral-100">
                        <span className="inline-flex items-center text-primary font-semibold text-lg group-hover:gap-3 gap-2 transition-all duration-300">
                          Explore {industry.title} Solutions
                          <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>
      
      {/* Stats Section */}
      <Section className="py-20 bg-primary text-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -right-40 w-80 h-80 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-0 -left-40 w-80 h-80 border border-white/10 rounded-full"></div>
        </div>
        
        <Container className="relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted Across Industries
            </h2>
            <p className="text-xl text-white/90">
              Our industry expertise delivers measurable results
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: 'Industries Served', value: '15+' },
              { label: 'Solutions Deployed', value: '500+' },
              { label: 'Client Success Rate', value: '98%' },
              { label: 'Average ROI', value: '3.5x' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-lg text-white/90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section className="py-20 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-4 h-4 bg-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-3 h-3 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-5 h-5 bg-white rounded-full"></div>
          <div className="absolute bottom-10 right-1/3 w-4 h-4 bg-white rounded-full"></div>
        </div>
        
        <Container className="relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Don't See Your 
                <span className="block md:inline text-transparent bg-gradient-to-r from-primary to-accent bg-clip-text">
                  {' '}Industry Listed?
                </span>
              </h2>
              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Our AI solutions can be adapted to virtually any industry. We specialize in 
                understanding unique challenges and creating custom implementations that drive results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  variant="primary"
                  size="lg" 
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary hover:to-secondary/90 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => window.location.href = '/contact'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h2V5a2 2 0 012-2h6a2 2 0 012 2v3z" />
                  </svg>
                  Contact Our Experts
                </Button>
                
                <Button 
                  variant="secondary"
                  size="lg"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                  onClick={() => window.location.href = '/tools/service-selector'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Try Our Solution Finder
                </Button>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default IndustriesPage;